import { Component, HostListener } from '@angular/core';
import { DebugService } from '../../services/debug/debug.service';
import { AuthSessionService } from 'src/app/modules/auth/services/auth-session/auth-session.service';
import { environment } from 'src/environments/environment';
import { VideoSaveStateService } from 'src/app/modules/video/services/video-save-state.service';
import { AudioService } from 'src/app/modules/audio/services/audio/audio.service';
import { ConfigurationService } from 'src/app/modules/configuration/services/configuration/configuration.service';
import {
  phonemeLookup,
  spanishPhonemeLookup,
  wordPartsLookup,
  PhonemeEntry,
} from 'src/app/modules/read-aloud/types/phoneme-types';
import { DebugView } from '../enums/debug-view';
import { animate, style, transition, trigger } from '@angular/animations';
import { AmiraTTSService } from 'src/app/modules/read-aloud/services/amira-tts/amira-tts.service';
import { AudioData, AudioDataSequence } from 'src/app/shared/interfaces';
import { FeatureFlagService } from 'src/app/modules/feature-flags/services/feature-flag/feature-flag.service';
import { FeatureFlags } from 'src/app/shared/enums/feature-flags';
import { StudentInboxService } from 'src/app/modules/student-inbox-api/services/student-inbox.service';
import { VerbalService } from 'src/app/modules/read-aloud/services/verbal.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'htc-debug-menu',
  templateUrl: './debug-menu.component.html',
  styleUrls: ['./debug-menu.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms ease', style({ opacity: 1 })),
      ]),
    ]),
  ],
  standalone: false,
})
export class DebugMenuComponent {
  readonly debugView = DebugView;
  currentView = DebugView.FEATURE_FLAGS;

  // Phoneme data
  readonly englishPhonemes = phonemeLookup;
  readonly spanishPhonemes = spanishPhonemeLookup;
  readonly wordParts = wordPartsLookup;
  selectedEnglishPhoneme: PhonemeEntry | undefined;
  selectedSpanishPhoneme: PhonemeEntry | undefined;
  selectedWordPart: PhonemeEntry | undefined;

  // Phonemic Breakdown data
  phonemicText = '';
  generatedAudioSequence: AudioDataSequence | undefined;
  isGeneratingSequence = false;
  isPlayingSequence = false;
  currentlyPlayingPhonemeIndex = -1; // Track which phoneme is currently playing

  // Video data
  selectedVideoPhoneme: PhonemeEntry | undefined;
  selectedVideoWordPart: PhonemeEntry | undefined;
  currentVideoUrl = '';
  isVideoPlaying = false;
  showVideoReplay = false;
  videoError = '';

  // Feature flag check
  get isAuthenticationEnabled(): boolean {
    return this.featureFlagService.isFlagEnabled(FeatureFlags.AUTHENTICATE);
  }

  // Custom sentence with tokens
  customTokenSentence = '';
  customTokenAudioResult: AudioData | AudioDataSequence | undefined;
  isCustomTokenLoading = false;
  isCustomTokenPlaying = false;
  customTokenError = '';

  isAudioSequence(
    obj: AudioData | AudioDataSequence | undefined
  ): obj is AudioDataSequence {
    return !!obj && (obj as AudioDataSequence).type === 'sequence';
  }

  constructor(
    public debugService: DebugService,
    private authSessionService: AuthSessionService,
    private videoSaveStateService: VideoSaveStateService,
    private audioService: AudioService,
    private configService: ConfigurationService,
    private amiraTTSService: AmiraTTSService,
    private featureFlagService: FeatureFlagService,
    private studentInboxService: StudentInboxService,
    private verbalService: VerbalService // <-- Inject VerbalService
  ) {}

  onPopupChange(open: boolean): void {
    if (!open) {
      this.debugService.showDebugMenu.next(false);
    }
  }

  @HostListener('document:touchstart', ['$event'])
  @HostListener('document:keydown.control.shift.d', ['$event'])
  @HostListener('document:keydown.control.alt.shift.d', ['$event'])
  @HostListener('document:keydown.control.shift.f', ['$event'])
  @HostListener('document:keydown.control.alt.shift.f', ['$event'])
  @HostListener('document:keydown.control.shift.k', ['$event'])
  @HostListener('document:keydown.control.alt.shift.k', ['$event'])
  @HostListener('document:keydown.control.shift.m', ['$event'])
  @HostListener('document:keydown.control.alt.shift.m', ['$event'])
  @HostListener('document:keydown.control.shift.c', ['$event'])
  @HostListener('document:keydown.control.alt.shift.c', ['$event'])
  // eslint-disable-next-line complexity
  openKBDebugMenu(event: Event): void {
    if (
      ('TouchEvent' in window &&
        event instanceof TouchEvent &&
        event.touches.length >= 3) ||
      event instanceof KeyboardEvent
    ) {
      if (
        !this.authSessionService.hasRole('jumpScene') &&
        environment.production
      ) {
        //if user doesn't have jumpScene acl, we don't want them to open any debug menus so early return.
        return;
      }
      if (event instanceof KeyboardEvent) {
        const key = (event as KeyboardEvent).key.toLowerCase();
        if (key === 'f') {
          this.setView(DebugView.FEATURE_FLAGS);
        } else if (key === 'k') {
          this.setView(DebugView.KB_DEBUG);
        } else if (key === 'c') {
          this.setView(DebugView.CLICKSTREAM_DEBUG);
        }
      }
      this.debugService.showDebugMenu.next(true);
    }
  }

  setVideoFinished(): void {
    this.videoSaveStateService.watchedFullVideo$.next(true);
  }

  async playEnglishPhoneme(): Promise<void> {
    console.log('playEnglishPhoneme', this.selectedEnglishPhoneme);
    if (this.selectedEnglishPhoneme) {
      const audioUrl = `${this.configService.config.amiraAssetDomain}/phonemes/${this.selectedEnglishPhoneme.filename}.mp3`;
      console.log('loading audioUrl', audioUrl);
      const audioData = {
        id: `debug-english-phoneme-${this.selectedEnglishPhoneme.filename}`,
        url: audioUrl,
        containsPhonemes: false,
      };

      console.log('initializing audioData', audioData);

      try {
        // Initialize the sound first
        const initialized = await this.audioService.initSound(audioData);
        console.log('audio initialized:', initialized);

        if (initialized) {
          console.log('playing audioData', audioData);
          await this.audioService.playSound(audioData, true, false, true);
        } else {
          console.log('audio already initialized, playing directly');
          await this.audioService.playSound(audioData, true, false, true);
        }
      } catch (error) {
        console.error('Error playing English phoneme:', error);
      }
    }
  }

  async playSpanishPhoneme(): Promise<void> {
    console.log('playSpanishPhoneme', this.selectedSpanishPhoneme);
    if (this.selectedSpanishPhoneme) {
      const audioUrl = `${this.configService.config.amiraAssetDomain}/phonemes/${this.selectedSpanishPhoneme.filename}.mp3`;
      console.log('loading audioUrl', audioUrl);
      const audioData = {
        id: `debug-spanish-phoneme-${this.selectedSpanishPhoneme.filename}`,
        url: audioUrl,
        containsPhonemes: false,
      };

      console.log('initializing audioData', audioData);

      try {
        // Initialize the sound first
        const initialized = await this.audioService.initSound(audioData);
        console.log('audio initialized:', initialized);

        if (initialized) {
          console.log('playing audioData', audioData);
          await this.audioService.playSound(audioData, true, false, true);
        } else {
          console.log('audio already initialized, playing directly');
          await this.audioService.playSound(audioData, true, false, true);
        }
      } catch (error) {
        console.error('Error playing Spanish phoneme:', error);
      }
    }
  }

  async playWordPart(): Promise<void> {
    console.log('playWordPart', this.selectedWordPart);
    if (this.selectedWordPart) {
      const audioUrl = `${this.configService.config.amiraAssetDomain}/phonemes/word-parts/${this.selectedWordPart.filename}.mp3`;
      console.log('loading audioUrl', audioUrl);
      const audioData = {
        id: `debug-word-part-${this.selectedWordPart.filename}`,
        url: audioUrl,
        containsPhonemes: false,
      };

      console.log('initializing audioData', audioData);

      try {
        // Initialize the sound first
        const initialized = await this.audioService.initSound(audioData);
        console.log('audio initialized:', initialized);

        if (initialized) {
          console.log('playing audioData', audioData);
          await this.audioService.playSound(audioData, true, false, true);
        } else {
          console.log('audio already initialized, playing directly');
          await this.audioService.playSound(audioData, true, false, true);
        }
      } catch (error) {
        console.error('Error playing word part:', error);
      }
    }
  }

  loadPhonemeVideo(): void {
    if (this.selectedVideoPhoneme) {
      this.currentVideoUrl = `${this.configService.config.amiraAssetDomain}/phonemes/videos/${this.selectedVideoPhoneme.filename}.mp4`;
      this.isVideoPlaying = false;
      this.showVideoReplay = false;
      this.videoError = '';
      console.log('Loading phoneme video:', this.currentVideoUrl);
    }
  }

  loadWordPartVideo(): void {
    if (this.selectedVideoWordPart) {
      this.currentVideoUrl = `${this.configService.config.amiraAssetDomain}/phonemes/word-parts/videos/${this.selectedVideoWordPart.filename}.mp4`;
      this.isVideoPlaying = false;
      this.showVideoReplay = false;
      this.videoError = '';
      console.log('Loading word part video:', this.currentVideoUrl);
    }
  }

  onVideoLoaded(): void {
    this.videoError = '';
    console.log('Video loaded successfully');
  }

  onVideoPlay(): void {
    this.isVideoPlaying = true;
    this.showVideoReplay = false;
    console.log('Video started playing');
  }

  onVideoEnded(): void {
    this.isVideoPlaying = false;
    this.showVideoReplay = true;
    console.log('Video ended');
  }

  onVideoError(): void {
    this.isVideoPlaying = false;
    this.showVideoReplay = true;
    this.videoError =
      'Failed to load video. Please check the URL and try again.';
    console.error('Video playback error');
  }

  onReplayClick(): void {
    const videoElement = document.getElementById(
      'debug-video'
    ) as HTMLVideoElement;
    if (videoElement) {
      videoElement.currentTime = 0;
      videoElement.play();
    }
  }

  async generatePhonemicSequence(): Promise<void> {
    if (!this.phonemicText.trim()) {
      console.warn('No text provided for phonemic breakdown');
      return Promise.resolve();
    }

    this.isGeneratingSequence = true;
    try {
      // Get word metadata and phonemes from StudentInboxService
      const wordMetadata = await this.studentInboxService.getWordMetadata(
        this.phonemicText
      );

      console.log('Word metadata received:', wordMetadata);

      // Generate audio sequence with phonemes from word metadata
      this.generatedAudioSequence = this.createAudioSequenceFromPhonemes(
        wordMetadata?.phonemes ?? []
      );

      console.log('Generated audio sequence:', this.generatedAudioSequence);
    } catch (error) {
      console.error('Error generating phonemic sequence:', error);
    } finally {
      this.isGeneratingSequence = false;
    }
  }

  async playPhonemicSequence(): Promise<void> {
    if (!this.generatedAudioSequence) {
      console.warn('No audio sequence to play');
      return Promise.resolve();
    }

    this.isPlayingSequence = true;
    try {
      // Load all parts of the sequence
      await this.loadAudioSequence(this.generatedAudioSequence);

      // Play the sequence
      await this.playAudioSequence(this.generatedAudioSequence);
    } catch (error) {
      console.error('Error playing phonemic sequence:', error);
    } finally {
      this.isPlayingSequence = false;
    }
  }

  async playIndividualPhoneme(phonemePart: AudioData): Promise<void> {
    if (!phonemePart) {
      console.warn('No phoneme to play');
      return Promise.resolve();
    }

    try {
      // Load the phoneme if not already loaded
      if (!this.audioService.isSoundLoaded(phonemePart.id)) {
        await this.audioService.initSound(
          phonemePart,
          (id: string) => console.log(`Individual phoneme started: ${id}`),
          (id: string) => console.log(`Individual phoneme ended: ${id}`),
          (id: string, error: unknown) =>
            console.error(`Individual phoneme error: ${id}`, error)
        );
      }

      // Play the phoneme
      await this.audioService.playSound(phonemePart, false, false, true);
    } catch (error) {
      console.error('Error playing individual phoneme:', error);
    }
  }

  async playFullWord(): Promise<void> {
    if (!this.phonemicText.trim()) {
      console.warn('No text provided for word playback');
      return;
    }

    try {
      // Get utterance from Amira TTS service
      const utterance = await this.amiraTTSService.getUtterance(
        this.phonemicText,
        false, // includeSpeechMarks
        'en-us', // locale
        10000, // timeout
        true // useGenerative
      );

      console.log('Utterance received:', utterance);

      // Create audio data from the utterance
      const audioData: AudioData = {
        id: `debug-full-word-${Date.now()}`,
        text: this.phonemicText,
        url: utterance.audioURL,
        containsPhonemes: false,
      };

      // Load and play the audio
      if (!this.audioService.isSoundLoaded(audioData.id)) {
        await this.audioService.initSound(
          audioData,
          (id: string) => console.log(`Full word started: ${id}`),
          (id: string) => console.log(`Full word ended: ${id}`),
          (id: string, error: unknown) =>
            console.error(`Full word error: ${id}`, error)
        );
      }

      await this.audioService.playSound(audioData, false, false, true);
    } catch (error) {
      console.error('Error playing full word:', error);
    }
  }

  async processCustomTokenSentence(): Promise<void> {
    this.customTokenError = '';
    this.customTokenAudioResult = undefined;
    if (!this.customTokenSentence.trim()) {
      this.customTokenError = 'Please enter a sentence.';
      return;
    }
    this.isCustomTokenLoading = true;
    try {
      // Use VerbalService to parse and generate audio object
      const result =
        this.verbalService.replaceLearningTargetAndSuccessCriteriaTokens(
          this.customTokenSentence,
          `debug-custom-token-${Date.now()}`
        );
      this.customTokenAudioResult = result;
    } catch (e) {
      this.customTokenError = 'Error processing sentence.';
    } finally {
      this.isCustomTokenLoading = false;
    }
  }

  /**
   * Ensures all plain text parts in an AudioData or AudioDataSequence have a TTS url.
   * Mutates the input object to add the url property.
   */
  private async synthesizeMissingUrls(
    audio: AudioData | AudioDataSequence
  ): Promise<void> {
    if (this.isAudioSequence(audio)) {
      for (const part of audio.parts) {
        if (!part.url && part.text) {
          try {
            const utterance = await this.amiraTTSService.getUtterance(
              part.text,
              false,
              'en-us',
              10000,
              true
            );
            part.url = utterance.audioURL;
          } catch (e) {
            console.error('Error synthesizing TTS for part:', part, e);
          }
        }
      }
    } else {
      if (!audio.url && audio.text) {
        try {
          const utterance = await this.amiraTTSService.getUtterance(
            audio.text,
            false,
            'en-us',
            10000,
            true
          );
          audio.url = utterance.audioURL;
        } catch (e) {
          console.error('Error synthesizing TTS for audio:', audio, e);
        }
      }
    }
  }

  // eslint-disable-next-line complexity
  async playCustomTokenAudio(): Promise<void> {
    if (!this.customTokenAudioResult) {
      this.customTokenError = 'No audio to play.';
      return;
    }
    this.isCustomTokenPlaying = true;
    this.customTokenError = '';
    try {
      console.log('CustomTokenAudioResult:', this.customTokenAudioResult);
      // Ensure all parts have a url before playback
      await this.synthesizeMissingUrls(this.customTokenAudioResult);
      if (this.isAudioSequence(this.customTokenAudioResult)) {
        const sequence = this.customTokenAudioResult;
        for (const part of sequence.parts) {
          if (!this.audioService.isSoundLoaded(part.id)) {
            try {
              await this.audioService.initSound(part);
            } catch (e) {
              console.error('Error initializing sound for part:', part, e);
            }
          }
        }
        // Play sequence, waiting for each part to finish
        for (const part of sequence.parts) {
          try {
            await this.audioService.playSound(part, false, false, true);
            // Wait for playback to finish for this part
            await new Promise<void>(resolve => {
              const sub: Subscription =
                this.audioService.playbackEndId.subscribe((id: string) => {
                  if (id === part.id) {
                    sub.unsubscribe();
                    resolve();
                  }
                });
            });
          } catch (e) {
            console.error('Error playing sound for part:', part, e);
          }
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } else {
        const audio = this.customTokenAudioResult;
        if (!this.audioService.isSoundLoaded(audio.id)) {
          try {
            await this.audioService.initSound(audio);
          } catch (e) {
            console.error('Error initializing sound:', audio, e);
          }
        }
        try {
          await this.audioService.playSound(audio, false, false, true);
        } catch (e) {
          console.error('Error playing sound:', audio, e);
        }
      }
    } catch (e) {
      this.customTokenError = 'Error playing audio.';
    } finally {
      this.isCustomTokenPlaying = false;
    }
  }

  comparePhonemes(
    phoneme1: PhonemeEntry | undefined,
    phoneme2: PhonemeEntry | undefined
  ): boolean {
    if (!phoneme1 || !phoneme2) {
      return phoneme1 === phoneme2;
    }
    return (
      phoneme1.phoneme === phoneme2.phoneme &&
      phoneme1.filename === phoneme2.filename
    );
  }

  setView(value: number): void {
    this.currentView = Object.values(DebugView).includes(value)
      ? value
      : DebugView.FEATURE_FLAGS;
  }

  private createAudioSequenceFromPhonemes(
    phonemes: string[]
  ): AudioDataSequence {
    const phonemeAudioData = this.amiraTTSService
      .getPhonemeSounds(phonemes)
      .map(props => ({
        id: `debug-phoneme-${props.friendlyName}-${Date.now()}`,
        text: props.phoneme,
        url: props.url,
        containsPhonemes: true,
      }));

    return {
      id: `debug-phonemic-sequence-${Date.now()}`,
      parts: phonemeAudioData,
      type: 'sequence',
      containsPhonemes: true,
      onStart: (): void => {
        console.log('Phonemic sequence started');
      },
      onEnd: (): void => {
        console.log('Phonemic sequence ended');
        this.isPlayingSequence = false;
      },
      onError: (error: Error): void => {
        console.error('Error in phonemic sequence:', error);
        this.isPlayingSequence = false;
      },
    } as AudioDataSequence;
  }

  private async loadAudioSequence(sequence: AudioDataSequence): Promise<void> {
    // Load each part of the sequence
    for (const part of sequence.parts) {
      if (!this.audioService.isSoundLoaded(part.id)) {
        await this.audioService.initSound(
          part,
          (id: string) => console.log(`Phoneme started: ${id}`),
          (id: string) => console.log(`Phoneme ended: ${id}`),
          (id: string, error: unknown) =>
            console.error(`Phoneme error: ${id}`, error)
        );
      }
    }
  }

  private async playAudioSequence(sequence: AudioDataSequence): Promise<void> {
    sequence.onStart?.();

    try {
      // Play each part in sequence
      for (let i = 0; i < sequence.parts.length; i++) {
        const part = sequence.parts[i];
        if (this.isPlayingSequence === false) break; // Stop if playback was cancelled

        // Set the currently playing phoneme index for highlighting
        this.currentlyPlayingPhonemeIndex = i;

        // Play the sound
        await this.audioService.playSound(part, false, false, true);

        // Wait for the sound to finish
        await new Promise<void>(resolve => {
          const subscription = this.audioService.playbackEndId.subscribe(
            (id: string) => {
              if (id === part.id) {
                subscription.unsubscribe();
                resolve();
              }
            }
          );
        });

        // Add a small pause between phonemes
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Reset the currently playing index when sequence ends
      this.currentlyPlayingPhonemeIndex = -1;
      sequence.onEnd?.();
    } catch (error) {
      console.error('Error playing sequence:', error);
      // Reset the currently playing index on error
      this.currentlyPlayingPhonemeIndex = -1;
      sequence.onError?.(error as Error);
      sequence.onEnd?.();
    }
  }
}
